import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { MonthlyCostDashboardDto, MonthlyCostItemTypeDashboardDto } from 'api/dashboard/dtos/MonthlyCostDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import Utils from 'common/services/Utils';
import dayjs from 'dayjs';
import Box, { Props as BoxProps } from 'common/components/box/Box';
import MonthlyCostChart from './MonthlyCostChart';
import { ReactComponent as ArrowDown } from 'assets/svg/chevron_down.svg';
import Button from 'common/components/button/Button';
import Colors from 'styles/export/colors.module.scss'
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import styles from './MonthlyCostChartBox.module.scss';

type Props = {
	// Legacy props (direct data passing)
	data?: MonthlyCostDashboardDto | null;
	isLoading?: boolean;
	onMonthlyCostDashboardClick?: (startDate?: Date) => void;
	// New props (self-fetch mode)
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
} & Partial<BoxProps>;

const MonthlyCostChartBox = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [fetchedData, setFetchedData] = useState<MonthlyCostDashboardDto | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);
	const [selectedYearIndex, setSelectedYearIndex] = useState<number | null>(null);

	const isLegacyMode = props.data !== undefined;
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('monthlyCostChart');
	const vehicleScope = vehicleScopeCriteria(useDashboardVehicleScope());
	const isControlled = props.filters != null;

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo, ...vehicleScope };
				const result = await DashboardService.getMonthlyCost(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode, dateFrom, dateTo]);

	const data = isLegacyMode ? props.data! : fetchedData;
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const allYears = Utils.flatten<MonthlyCostItemTypeDashboardDto>((data?.items || []).map(x => x.items)).map(x =>
		x.date.getFullYear(),
	);
	const yearsOptions = allYears
		.filter((year, i, self) => self.findIndex(d => d === year) === i)
		.sort((a, b) => a - b);
	const existsMoreThanOneYear = allYears.every((year, _, arr) => year === arr[0]) === false;

	const selectedYear = selectedYearIndex !== null ? yearsOptions[selectedYearIndex] : null;

	useEffect(() => {
		if (existsMoreThanOneYear) {
			const currYear = dayjs().year();
			const currYearExistIndex = yearsOptions.findIndex(year => year === currYear);

			setSelectedYearIndex((currYearExistIndex !== null && currYearExistIndex >= 0) ? currYearExistIndex : (yearsOptions.length - 1));
		} else {
			setSelectedYearIndex(null);
		}
	}, [data]);

	if (!data && !isLoading) {
		return null;
	}

	const onPreviousYear = () => {
		if (selectedYearIndex !== null && selectedYearIndex > 0) {
			setSelectedYearIndex(selectedYearIndex - 1);
		}
	};

	const onNextYear = () => {
		if (selectedYearIndex !== null && selectedYearIndex < yearsOptions.length - 1) {
			setSelectedYearIndex(selectedYearIndex + 1);
		}
	};

	const yearOptions = existsMoreThanOneYear && selectedYear
		? (
			<div className={styles.optionsContainer}>
				<Button preset="primary" size="extraSmall" bw={false} onClick={onPreviousYear} disabled={selectedYear === yearsOptions[0]}>
					<ArrowDown width={12} height={12} fill={Colors.white} style={{ transform: 'rotate(90deg)' }} />
				</Button>
				<div className={styles.year}>
					{selectedYear}
				</div>
				<Button preset="primary" size="extraSmall" bw={false} onClick={onNextYear} disabled={selectedYear === yearsOptions[yearsOptions.length - 1]}>
					<ArrowDown width={12} height={12} fill={Colors.white} style={{ transform: 'rotate(-90deg)' }} />
				</Button>
			</div>
		)
		: undefined;

	const content = isLoading ? <div className={styles.centerAlign}> <LoadingSpinner /></div> : data && <MonthlyCostChart data={data} selectedYear={isLegacyMode ? selectedYear : null} onMonthlyCostDashboardClick={props.onMonthlyCostDashboardClick} />;

	if (isLegacyMode) {
		return (
			<Box
				className={props.className}
				bodyClassName={props.bodyClassName}
				title={props.title}
				renderOptions={yearOptions}
			>
				{content}
			</Box>
		);
	}

	const periodTotal = (data?.items || []).reduce(
		(sum, typeItem) => sum + typeItem.items.reduce((s, x) => s + (x.value || 0), 0),
		0,
	);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.monthly_cost') as string}
			subTitleTooltip={t('dashboard.monthly_cost_tooltip' as any) as string}
			renderOptions={isControlled ? undefined : <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			<KpiHeader
				value={Utils.formatCurrency(periodTotal)}
				subLabel={t('dashboard.period_total' as any) as string}
				currentTotal={periodTotal}
				previousTotal={null}
				previousRange={null}
			/>
			{content}
		</Box>
	);
};

export default MonthlyCostChartBox;
